// src/utils/artTypeTaxonomy.js
//
// Taxonomie « type d'art » (axe DÉPARTEMENT / PÉRIODE), inspirée des
// départements des maisons de ventes (Christie's, Sotheby's, Bonhams, Millon).
// But : ramener le champ libre `artworks.typeOfArt` (saisie hétérogène FR/EN,
// mouvements, casse, noms de départements…) à un jeu FINI de clés canoniques.
//
// ⚠️ Les clés canoniques REUTILISENT le vocabulaire du sélecteur existant
// (`artworks.typeOfArt.*` dans les locales) pour ne pas créer de taxonomie
// parallèle. 3 clés ont été ajoutées : nineteenth_century_art, prints_multiples,
// digital_art (+ unknown pour le vide).
//
// Règles de l'arborescence :
//  - Équivalences volontaires :
//      « Art contemporain » ≈ « 20th & 21st Century » ; « Post-War » a sa clé.
//      « Art moderne »       ≈ « Impressionist & Modern » ; « Impressionnisme » a sa clé.
//      « Arts anciens »      ≈ « Old Master Paintings » ≈ « Classic Art ».
//  - Les MOUVEMENTS sont rattachés à leur période :
//      impressionnisme → impressionism ; cubisme/surréalisme/expressionnisme → moderne ;
//      minimalisme/conceptuel/abstraction/pop → contemporain ; street art → street_art.
//  - Les valeurs qui sont en réalité des MÉDIUMS (peinture, sculpture, dessin…)
//    tombent en "other" : le médium a déjà sa propre taxonomie/graphe.
//  - Vide/inconnu → "unknown".
//
// Étendre = ajouter une entrée dans EXACT (correspondance exacte, prioritaire)
// ou CONTAINS (sous-chaîne, pour les libellés composés).

export const ART_TYPE_UNKNOWN = "unknown";

// Clés canoniques, dans l'ordre d'affichage souhaité (légende du graphe).
export const ART_TYPE_CANONICAL = [
  "contemporary_art",
  "street_art",
  "modern_art",
  "impressionism",
  "nineteenth_century_art",
  "old_masters",
  "antiquities",
  "asian_art",
  "middle_eastern_art",
  "african_art",
  "latin_american_art",
  "tribal_art",
  "photography",
  "prints_multiples",
  "digital_art",
  "design",
  "decorative_arts",
  "other",
  "unknown",
];

// Libellés FR de secours (si la clé i18n `artworks.typeOfArt.<clé>` manque).
export const ART_TYPE_FALLBACK_FR = {
  contemporary_art: "Art contemporain",
  street_art: "Street art",
  modern_art: "Art moderne",
  impressionism: "Impressionnisme",
  nineteenth_century_art: "Art du 19e siècle",
  old_masters: "Arts anciens",
  antiquities: "Antiquités",
  asian_art: "Art asiatique",
  middle_eastern_art: "Art du Moyen-Orient",
  african_art: "Art africain",
  latin_american_art: "Art latino-américain",
  tribal_art: "Art tribal",
  photography: "Photographie",
  prints_multiples: "Estampes & multiples",
  digital_art: "Art numérique / NFT",
  design: "Design",
  decorative_arts: "Arts décoratifs",
  other: "Autre",
  unknown: "Non renseigné",
};

// Normalisation identique au reste de l'app (NFD, minuscules, underscores).
const norm = (v) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/&/g, " and ")
    .replace(/['’`]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// Équivalents EXACTS (valeur normalisée → clé canonique). Prioritaire.
const EXACT = {
  // Contemporain
  art_contemporain: "contemporary_art",
  contemporain: "contemporary_art",
  contemporary: "contemporary_art",
  contemporary_art: "contemporary_art",
  "20th_and_21st_century_art": "contemporary_art",
  now: "contemporary_art",
  ultra_contemporain: "contemporary_art",
  // Après-guerre — FUSIONNÉ dans « contemporain » (décision Vanessa : on ne
  // sépare pas post-war et contemporain, cf. « Post-War & Contemporary »).
  post_war: "contemporary_art",
  postwar: "contemporary_art",
  post_war_and_contemporary: "contemporary_art",
  apres_guerre: "contemporary_art",
  art_d_apres_guerre: "contemporary_art",
  postwar_art: "contemporary_art",
  // Street / urbain
  street_art: "street_art",
  urban_art: "street_art",
  urbain: "street_art",
  graffiti: "street_art",
  // Moderne (+ mouvements du début 20e)
  art_moderne: "modern_art",
  moderne: "modern_art",
  modern: "modern_art",
  modern_art: "modern_art",
  impressionist_and_modern: "modern_art",
  cubisme: "modern_art",
  surrealisme: "modern_art",
  expressionnisme: "modern_art",
  fauvisme: "modern_art",
  dadaisme: "modern_art",
  art_deco: "modern_art",
  art_nouveau: "modern_art",
  // Impressionnisme (clé dédiée)
  impressionnisme: "impressionism",
  impressionist: "impressionism",
  impressionism: "impressionism",
  // 19e siècle
  nineteenth_century_art: "nineteenth_century_art",
  art_du_19e_siecle: "nineteenth_century_art",
  orientalisme: "nineteenth_century_art",
  orientalist: "nineteenth_century_art",
  // Maîtres anciens
  art_ancien: "old_masters",
  ancien: "old_masters",
  old_masters: "old_masters",
  old_master_paintings: "old_masters",
  classic_art: "old_masters",
  maitres_anciens: "old_masters",
  arts_anciens: "old_masters",
  // Antiquités
  antiquites: "antiquities",
  antiquities: "antiquities",
  // Asiatique
  art_asiatique: "asian_art",
  asian_art: "asian_art",
  asian: "asian_art",
  chinese_art: "asian_art",
  japanese_art: "asian_art",
  korean_art: "asian_art",
  indian_art: "asian_art",
  south_asian_art: "asian_art",
  // Moyen-Orient / islamique
  middle_eastern_art: "middle_eastern_art",
  moyen_orient: "middle_eastern_art",
  islamic_art: "middle_eastern_art",
  islamic: "middle_eastern_art",
  // Africain / océanien
  african_art: "african_art",
  african: "african_art",
  oceanic_art: "african_art",
  african_and_oceanic_art: "african_art",
  // Latino-américain
  latin_american_art: "latin_american_art",
  latin_american: "latin_american_art",
  // Tribal / premiers arts
  tribal_art: "tribal_art",
  art_tribal: "tribal_art",
  aboriginal_art: "tribal_art",
  native_american: "tribal_art",
  // Photographie
  photographie: "photography",
  photography: "photography",
  photographs: "photography",
  photo: "photography",
  // Estampes & multiples
  prints_multiples: "prints_multiples",
  estampe: "prints_multiples",
  estampes: "prints_multiples",
  gravure: "prints_multiples",
  prints: "prints_multiples",
  prints_and_multiples: "prints_multiples",
  multiples: "prints_multiples",
  lithographie: "prints_multiples",
  serigraphie: "prints_multiples",
  // Numérique / NFT
  art_numerique: "digital_art",
  numerique: "digital_art",
  digital: "digital_art",
  digital_art: "digital_art",
  nft: "digital_art",
  nfts_and_digital_art: "digital_art",
  crypto_art: "digital_art",
  // Design / arts décoratifs
  design: "design",
  decorative_arts: "decorative_arts",
  arts_decoratifs: "decorative_arts",
  mobilier: "decorative_arts",
  furniture: "decorative_arts",
  // Mouvements contemporains → contemporain
  minimalisme: "contemporary_art",
  minimalism: "contemporary_art",
  art_conceptuel: "contemporary_art",
  conceptuel: "contemporary_art",
  pop_art: "contemporary_art",
  abstraction: "contemporary_art",
  arte_povera: "contemporary_art",
  land_art: "contemporary_art",
  op_art: "contemporary_art",
  // Médiums mal saisis dans le champ « type » → autre (le médium a son graphe)
  peinture: "other",
  painting: "other",
  sculpture: "other",
  dessin: "other",
  drawing: "other",
  mixed_media: "other",
  technique_mixte: "other",
  ceramique: "other",
  installation: "other",
  video: "other",
  collage: "other",
  autre: "other",
  other: "other",
  // Auto-correspondance de la clé « inconnu » (idempotence lecture/affichage).
  unknown: "unknown",
};

// Détection par SOUS-CHAÎNE (libellés composés). Ordre = spécificité décroissante.
const CONTAINS = [
  ["post_war", "contemporary_art"],
  ["apres_guerre", "contemporary_art"],
  ["street", "street_art"],
  ["urbain", "street_art"],
  ["19e", "nineteenth_century_art"],
  ["19th", "nineteenth_century_art"],
  ["orientalis", "nineteenth_century_art"],
  ["antiquit", "antiquities"],
  ["old_master", "old_masters"],
  ["maitre_ancien", "old_masters"],
  ["nft", "digital_art"],
  ["numerique", "digital_art"],
  ["digital", "digital_art"],
  ["photograph", "photography"],
  ["estampe", "prints_multiples"],
  ["gravure", "prints_multiples"],
  ["serigraph", "prints_multiples"],
  ["lithograph", "prints_multiples"],
  ["impressionn", "impressionism"],
  ["contempor", "contemporary_art"],
  ["21st", "contemporary_art"],
  ["minimalis", "contemporary_art"],
  ["conceptuel", "contemporary_art"],
  ["abstrait", "contemporary_art"],
  ["abstraction", "contemporary_art"],
  ["expressionn", "modern_art"],
  ["cubis", "modern_art"],
  ["surrealis", "modern_art"],
  ["moderne", "modern_art"],
  ["modern", "modern_art"],
  ["asiatique", "asian_art"],
  ["islamic", "middle_eastern_art"],
  ["moyen_orient", "middle_eastern_art"],
  ["africa", "african_art"],
  ["tribal", "tribal_art"],
  ["aborig", "tribal_art"],
  ["latino", "latin_american_art"],
];

/**
 * Ramène une valeur libre de « type d'art » à sa clé canonique.
 * @param {string} value
 * @param {{strict?: boolean}} [opts] strict=true → renvoie `null` au lieu de
 *   "unknown"/"other" quand rien ne correspond (utile aux flux d'ÉCRITURE
 *   OCR/IA : ne pas écraser une saisie par « Autre »).
 * @returns {string|null} une des ART_TYPE_CANONICAL, ou null en mode strict.
 */
export function getArtTypeCanonicalKey(value, { strict = false } = {}) {
  const k = norm(value);
  if (!k) return strict ? null : ART_TYPE_UNKNOWN;
  if (EXACT[k]) return EXACT[k];
  for (const [needle, canon] of CONTAINS) {
    if (k.includes(needle)) return canon;
  }
  return strict ? null : "other";
}
